import React, { useState, useEffect } from 'react';
import { withRouter } from '../../common/withRouter';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Notification, ButtonOBSOLETED, Loader, Message } from '../../web.common';
import withApi from '../higherOrderComponents/withApi';
import { LayoutContext } from '../layout/siteLayout';
import PageTop from '../layout/PageTop';
import { CATEGORY_OPTIONS, LANGUAGE_OPTIONS } from './seoOptions';

const SeoTextAddInternal = ({ api, history, match }) => {
  const id = match.params.id;
  const isEditMode = !!id;

  const initialValue = {
    AreaSlug: '',
    Category: '8192', // Padel
    Language: '1',    // Danish
    MetaTitle: '',
    MetaDescription: '',
    MetaKeywords: '',
    H1Title: '',
    TopText: '',
    ExtraText: ''
  };

  const [formData, setFormData] = useState(initialValue);
  const [notification, setNotification] = useState(null);
  const [isLoading, setIsLoading] = useState(isEditMode);

  useEffect(() => {
    if (isEditMode) {
      loadData();
    }
  }, [id]);

  const loadData = async () => {
    try {
      const row = await api.seoTexts.getSeoText(id);
      setFormData({
        AreaSlug: row.areaSlug || '',
        Category: String(row.category),
        Language: String(row.language),
        MetaTitle: row.metaTitle || '',
        MetaDescription: row.metaDescription || '',
        MetaKeywords: row.metaKeywords || '',
        H1Title: row.h1Title || '',
        TopText: row.topText || '',
        ExtraText: row.extraText || ''
      });
      setIsLoading(false);
    } catch (e) {
      setNotification(new Message('error', 'Unable to load SEO text data'));
      setIsLoading(false);
    }
  };

  const formLabelStyle = { display: "table-cell", width: "20%", whiteSpace: "nowrap", fontWeight: "bold", verticalAlign: "top" };
  const formSpanStyle = { display: "table-cell" };
  const rowStyle = { display: "table", width: "100%", margin: "auto", marginBottom: "0" };
  const containerStyle = { width: '80%', margin: 'auto', backgroundColor: 'white', border: "1px solid #e5e5e5", padding: "1.2rem" };
  const helpTextStyle = { fontSize: '0.85rem', color: '#666', fontStyle: 'italic', marginTop: '5px' };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (formData.AreaSlug.trim() === '') {
      setNotification(new Message('error', 'Area Slug is required.'));
      return;
    }

    const payload = {
      AreaSlug: formData.AreaSlug.trim(),
      Category: parseInt(formData.Category, 10),
      Language: parseInt(formData.Language, 10),
      MetaTitle: formData.MetaTitle.trim() || null,
      MetaDescription: formData.MetaDescription.trim() || null,
      MetaKeywords: formData.MetaKeywords.trim() || null,
      H1Title: formData.H1Title.trim() || null,
      TopText: formData.TopText.trim() || null,
      ExtraText: formData.ExtraText.trim() || null
    };

    try {
      if (isEditMode) {
        payload.Id = parseInt(id, 10);
        const result = await api.seoTexts.updateSeoText(payload);
        if (result && result.success === false) {
          setNotification(new Message('error', result.message || 'Unable to save'));
          return;
        }
        setNotification(new Message('success', 'SEO text updated. Live immediately.'));
      } else {
        const result = await api.seoTexts.createSeoText(payload);
        if (result && result.success === false) {
          setNotification(new Message('error', result.message || 'Unable to save'));
          return;
        }
        setNotification(new Message('success', 'SEO text created. Live immediately.'));
        if (result && result.id) {
          setTimeout(() => history.push(`/seo-text/${result.id}`), 1500);
        }
      }
    } catch (e) {
      let errorMessage = 'Unable to save SEO text';
      if (e.payload?.message) errorMessage = e.payload.message;
      else if (e.message && e.message !== 'Server returned error: ') errorMessage = e.message.replace('Server returned error: ', '');
      setNotification(new Message('error', errorMessage));
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this SEO text? This cannot be undone.')) {
      return;
    }
    try {
      await api.seoTexts.deleteSeoText(parseInt(id, 10));
      setNotification(new Message('success', 'SEO text deleted.'));
      setTimeout(() => history.push('/seo-texts'), 1500);
    } catch (e) {
      setNotification(new Message('error', 'Unable to delete SEO text'));
    }
  };

  if (isLoading) {
    return <Loader hasLoaded={false}>Loading...</Loader>;
  }

  return (
    <LayoutContext.Consumer>
      {() => (
        <React.Fragment>
          <Notification newMessage={notification} />
          <PageTop
            documentTitle={isEditMode ? 'Edit SEO Text' : 'New SEO Text'}
            documentDescription={isEditMode ? 'Edit SEO Text' : 'New SEO Text'}
          />

          <h2 className='main-page-header main-page-header-with-subheader'>
            {isEditMode ? 'Edit SEO Text' : 'New SEO Text'}
          </h2>
          <h5 className='main-page-subheader'>
            <i>{isEditMode ? `Editing ${formData.AreaSlug}` : 'Create a new per-area/category SEO entry'}</i>
          </h5>
          <br />
          <div style={containerStyle}>
            <form onSubmit={handleSubmit}>
              <div style={rowStyle}>
                <label htmlFor="areaSlug" style={formLabelStyle}>{'Area Slug*'}</label>
                <span style={formSpanStyle}>
                  <input
                    name="areaSlug"
                    id="areaSlug"
                    placeholder='e.g., koebenhavn'
                    value={formData.AreaSlug}
                    onChange={({ target }) => setFormData({ ...formData, AreaSlug: target.value })}
                    style={{ width: '100%' }}
                  />
                  <div style={helpTextStyle}>The area slug as it appears in the URL (lowercased on save). Must match the routed area.</div>
                </span>
              </div>
              <hr />

              <div style={rowStyle}>
                <label htmlFor="category" style={formLabelStyle}>{'Category*'}</label>
                <span style={formSpanStyle}>
                  <select
                    name="category"
                    id="category"
                    value={formData.Category}
                    onChange={({ target }) => setFormData({ ...formData, Category: target.value })}
                    style={{ width: '100%', padding: '5px' }}
                  >
                    {CATEGORY_OPTIONS.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
                  </select>
                </span>
              </div>
              <hr />

              <div style={rowStyle}>
                <label htmlFor="language" style={formLabelStyle}>{'Language*'}</label>
                <span style={formSpanStyle}>
                  <select
                    name="language"
                    id="language"
                    value={formData.Language}
                    onChange={({ target }) => setFormData({ ...formData, Language: target.value })}
                    style={{ width: '100%', padding: '5px' }}
                  >
                    {LANGUAGE_OPTIONS.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
                  </select>
                </span>
              </div>
              <hr />

              <div style={rowStyle}>
                <label htmlFor="metaTitle" style={formLabelStyle}>{'Meta Title'}</label>
                <span style={formSpanStyle}>
                  <input
                    name="metaTitle"
                    id="metaTitle"
                    value={formData.MetaTitle}
                    onChange={({ target }) => setFormData({ ...formData, MetaTitle: target.value })}
                    style={{ width: '100%' }}
                    maxLength={200}
                  />
                  <div style={helpTextStyle}>Leave empty to fall back to the templated title.</div>
                </span>
              </div>
              <hr />

              <div style={rowStyle}>
                <label htmlFor="metaDescription" style={formLabelStyle}>{'Meta Description'}</label>
                <span style={formSpanStyle}>
                  <textarea
                    name="metaDescription"
                    id="metaDescription"
                    value={formData.MetaDescription}
                    onChange={({ target }) => setFormData({ ...formData, MetaDescription: target.value })}
                    style={{ width: '100%', minHeight: '60px' }}
                    maxLength={400}
                  />
                </span>
              </div>
              <hr />

              <div style={rowStyle}>
                <label htmlFor="metaKeywords" style={formLabelStyle}>{'Meta Keywords'}</label>
                <span style={formSpanStyle}>
                  <textarea
                    name="metaKeywords"
                    id="metaKeywords"
                    value={formData.MetaKeywords}
                    onChange={({ target }) => setFormData({ ...formData, MetaKeywords: target.value })}
                    style={{ width: '100%', minHeight: '60px' }}
                    maxLength={400}
                  />
                  <div style={helpTextStyle}>Comma-separated keywords.</div>
                </span>
              </div>
              <hr />

              <div style={rowStyle}>
                <label htmlFor="h1Title" style={formLabelStyle}>{'H1 Heading'}</label>
                <span style={formSpanStyle}>
                  <input
                    name="h1Title"
                    id="h1Title"
                    value={formData.H1Title}
                    onChange={({ target }) => setFormData({ ...formData, H1Title: target.value })}
                    style={{ width: '100%' }}
                    maxLength={200}
                  />
                  <div style={helpTextStyle}>Leave empty to fall back to the templated H1 on the page.</div>
                </span>
              </div>
              <hr />

              <div style={rowStyle}>
                <label htmlFor="topText" style={formLabelStyle}>{'Top Text (Markdown)'}</label>
                <span style={formSpanStyle}>
                  <textarea
                    name="topText"
                    id="topText"
                    value={formData.TopText}
                    onChange={({ target }) => setFormData({ ...formData, TopText: target.value })}
                    style={{ width: '100%', minHeight: '150px', fontFamily: 'monospace' }}
                  />
                  <div style={helpTextStyle}>Markdown copy rendered at the top of the page, above the search results. Leave empty for none.</div>
                </span>
              </div>
              <hr />

              <div style={rowStyle}>
                <label htmlFor="extraText" style={formLabelStyle}>{'Body (Markdown)'}</label>
                <span style={formSpanStyle}>
                  <textarea
                    name="extraText"
                    id="extraText"
                    value={formData.ExtraText}
                    onChange={({ target }) => setFormData({ ...formData, ExtraText: target.value })}
                    style={{ width: '100%', minHeight: '300px', fontFamily: 'monospace' }}
                  />
                  <div style={helpTextStyle}>Markdown body rendered under the search results. Leave empty for a meta-only entry.</div>
                </span>
              </div>
              <hr />

              <div style={{ textAlign: 'center', marginTop: '20px' }}>
                <ButtonOBSOLETED
                  buttonColor="primary"
                  type="submit"
                  buttonContent={isEditMode ? "Update" : "Create"}
                  ariaLabel={isEditMode ? "Update SEO text" : "Create SEO text"}
                  disabled={false}
                />
                {isEditMode && (
                  <span style={{ marginLeft: '15px' }}>
                    <ButtonOBSOLETED
                      buttonColor="gray"
                      type="button"
                      onClick={handleDelete}
                      buttonContent="Delete"
                      ariaLabel="Delete SEO text"
                      disabled={false}
                    />
                  </span>
                )}
                <span style={{ marginLeft: '15px' }}>
                  <ButtonOBSOLETED
                    buttonColor="gray"
                    type="button"
                    onClick={() => history.push('/seo-texts')}
                    buttonContent="Cancel"
                    ariaLabel="Cancel"
                    disabled={false}
                  />
                </span>
              </div>
            </form>
          </div>
        </React.Fragment>
      )}
    </LayoutContext.Consumer>
  );
};

SeoTextAddInternal.propTypes = {
  api: PropTypes.object,
  history: PropTypes.object,
  match: PropTypes.object,
  isLoggedIn: PropTypes.bool
};

const SeoTextAdd = withRouter(
  withApi(
    connect(({ user }) => ({ isLoggedIn: !!user }), {})(SeoTextAddInternal)
  )
);

SeoTextAdd.requiresLogin = true;

export default SeoTextAdd;
